import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Preferences } from '@capacitor/preferences';
import {
  RECIPE_ACTIONS,
  FetchRecipeProfileImageResponse,
  selectRecipeImageSrc,
  MPP_ACTIONS,
  MPPTracingConstants,
  baseMPPTransform,
} from '@usf/ngrx-tools';
import { Platform } from '@ionic/angular';
import { Device } from '@capacitor/device';

@Injectable({
  providedIn: 'root',
})
export class RecipeImageService {
  private imageMap = new Map<string, string | null>();
  private loadingMap = new Map<string, boolean>();
  private readonly BATCH_SIZE = 10;
  private readonly CACHE_KEY_PREFIX = 'recipe_image_';
  private RECIPE_IMAGE_STROAGE = 'recipe/photos';
  private deviceInfo;

  constructor(
    private store: Store,
    private platform: Platform,
  ) {
    Device.getInfo().then(info => {
      this.deviceInfo = info;
    });
  }

  // public async loadPhotos(customerNumber: number) {
  //   const photosSaved = await this.readOutAllSavedPhotos(customerNumber);
  //   // dispatch out to save to image references in state
  // }

  // public async clearPhotos() {
  //   // dispatch action to clear out image references (customer switch)
  // }

  // public async readOutAllSavedPhotos(customerNumber: number) {
  //   let photos = [];
  //   try {
  //     const photoList = await Preferences.get({
  //       key: this.RECIPE_IMAGE_STROAGE + '/' + customerNumber,
  //     });
  //     photos.push(JSON.parse(photoList.value) || []);
  //   } catch (ignore) {}
  //   return photos;
  // }

  // public saveDownUpdatedPhotos(data: photoKey[]) {}

  public getImageUrl(recipeId: string): string | null {
    return this.imageMap.get(recipeId) ?? null;
  }

  public isLoading(recipeId: string): boolean {
    return this.loadingMap.get(recipeId) ?? false;
  }

  public async saveDownImages(recipes: FetchRecipeProfileImageResponse[]) {
    const recipeImages = [];
    for (const recipe of recipes) {
      if (!!recipe.imageBase64) {
        const transformedData = await this.convertBase64ToWebViewPath(
          recipe.imageBase64,
        );
        recipeImages.push({
          recipeID: recipe.recipeId,
          lastDownloaded: new Date().toISOString(),
          src: transformedData.url,
        });
      }
    }

    this.store.dispatch(
      RECIPE_ACTIONS.setImageURIValues({
        recipeImages,
      }),
    );
  }

  public async getRecipeImageUris(recipeIds: string[]) {
    const currentlySavedRecipeImages = await firstValueFrom(
      this.store.select(selectRecipeImageSrc()),
    );

    // Helper to split into batches
    const chunkArray = <T>(arr: T[], size: number): T[][] => {
      const result: T[][] = [];
      for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
      }
      return result;
    };

    // Find missing recipeIds
    const missingRecipeRequests: {
      recipeId: string;
      imageWaterMark: string | Date;
    }[] = [];
    for (const recipeId of recipeIds) {
      const data = currentlySavedRecipeImages[recipeId];
      const src = data?.src;
      if (!src) {
        missingRecipeRequests.push({
          recipeId,
          imageWaterMark: '',
        });
      }
    }

    if (missingRecipeRequests.length > 0) {
      // Batch the requests by BATCH_SIZE
      const batches = chunkArray(missingRecipeRequests, this.BATCH_SIZE);
      for (const batch of batches) {
        this.store.dispatch(
          RECIPE_ACTIONS.getRecipeProfileImagesFromS3({
            getImagesRequest: batch,
          }),
        );
        // Add a 100ms delay between batches for UI smoothness
        await new Promise(res => setTimeout(res, 100));
      }
    }
  }

  public async convertBase64ToWebViewPath(
    base64string: string,
    fileExtension?: string,
  ): Promise<{ url: string; decodedBase64: string; extension: string }> {
    const prefixRegex = /^data:image\/\w+;base64,/;
    if (!prefixRegex.test(base64string)) {
      throw new Error('Invalid base64 image format.');
    }

    let base64Payload = base64string.replace(prefixRegex, '');
    const match = base64string.match(prefixRegex);
    const extension = match?.[1]?.toLowerCase() || 'png';

    // If it's double encoded (from S3), decode it once
    try {
      base64Payload = atob(base64Payload);
    } catch (e) {
      console.warn(
        'PhotoService: Base64 decode failed, assuming already single encoded.',
      );
    }
    if (base64Payload.includes('base64,')) {
      return this.convertToUri(base64Payload.split('base64,')[1], extension);
    } else {
      return this.convertToUri(base64Payload, extension);
    }
  }

  public async convertToUri(
    dedcodedbase64: string,
    extension: string,
  ): Promise<{ url: string; decodedBase64: string; extension: string }> {
    try {
      const binary = atob(dedcodedbase64);
      const buffer = new ArrayBuffer(binary.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < binary.length; i++) {
        view[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([buffer], { type: `image/${extension}` });

      // If we ever get to caching again we will need these to have alternative function paths, for now they are just the same thing.
      if (this.platform.is('hybrid')) {
        const objUrl = URL.createObjectURL(blob);
        return {
          url: objUrl,
          decodedBase64: dedcodedbase64,
          extension: extension,
        };
      } else {
        const objUrl = URL.createObjectURL(blob);
        return {
          url: objUrl,
          decodedBase64: dedcodedbase64,
          extension: extension,
        };
      }
    } catch (error) {
      console.error('PhotoService: Error processing image:', error);
      throw error;
    }
  }

  public async uploadRecipeImage(
    recipeId: string,
    imageData: { fileName: string; type: string; value: string },
  ): Promise<void> {
    if (!recipeId) return;

    try {
      this.store.dispatch(
        MPP_ACTIONS.uploadImage({
          tracking: {
            tracing: {
              data: {
                traceContext: MPPTracingConstants.uploadImage,
                isEndOfTrace: false,
                isStartOfTrace: true,
                attributes: {
                  event: MPPTracingConstants.uploadImageEvent,
                  spanName: MPPTracingConstants.uploadImageSpan,
                },
              },
              transformFunc: baseMPPTransform,
            },
          },
        }),
      );
      this.store.dispatch(
        RECIPE_ACTIONS.uploadRecipeProfileImageToS3({
          uploadImage: {
            recipeId,
            profileImage: {
              name: imageData.fileName,
              type: imageData.type,
              value: imageData.value,
            },
          },
        }),
      );
    } catch (error) {
      console.error('Image upload error:', error);
    }
  }

  private checkLocalCache(recipeId: string): Observable<string> {
    return from(
      Preferences.get({ key: `${this.CACHE_KEY_PREFIX}${recipeId}` }),
    ).pipe(
      map(result => {
        if (!result?.value) return '';

        try {
          const data = JSON.parse(result.value);
          if (data?.base64) {
            return `data:image/${data.extension || 'jpeg'};base64,${data.base64}`;
          }
          return data?.webviewPath || '';
        } catch (e) {
          return '';
        }
      }),
      catchError(() => of('')),
    );
  }
}
